import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/app/providers/ToastProvider";
import {
  ENV_DISPLAY_KEYS,
  fetchEnv,
  fetchNotifications,
  fetchUserGlobal,
  pingSystem,
  setNotificationEnabled,
  type NotificationConf,
} from "@/features/settings/api";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

/** 设置：环境摘要 + 通知开关（不编辑密钥） */
export function SettingsPage() {
  const { toast } = useToast();
  const [env, setEnv] = useState<Record<string, unknown> | null>(null);
  const [userGlobal, setUserGlobal] = useState<Record<string, unknown> | null>(
    null,
  );
  const [notifications, setNotifications] = useState<NotificationConf[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [e, u, n] = await Promise.all([
        fetchEnv().catch((err) => {
          throw err;
        }),
        fetchUserGlobal().catch(() => null),
        fetchNotifications().catch(() => []),
      ]);
      setEnv(e);
      setUserGlobal(u);
      setNotifications(n);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "加载失败（部分项需超级用户）",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleNotify(n: NotificationConf) {
    if (!n.name) return;
    setBusyName(n.name);
    try {
      await setNotificationEnabled(n.name, !n.enabled);
      toast(n.enabled ? "已关闭通知" : "已开启通知", "success");
      const next = await fetchNotifications();
      setNotifications(next);
    } catch (e) {
      toast(e instanceof Error ? e.message : "更新失败", "error");
    } finally {
      setBusyName(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="设置"
        description="环境摘要 · 通知开关 · 密钥请在服务端配置"
        actions={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                void pingSystem()
                  .then(() => toast("服务正常", "success"))
                  .catch((e) =>
                    toast(e instanceof Error ? e.message : "不可达", "error"),
                  );
              }}
            >
              Ping
            </Button>
            <Button variant="ghost" onClick={() => void load()} disabled={loading}>
              刷新
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="mb-4 flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4">
          <h2 className="text-[13px] font-medium text-fg-muted">系统环境</h2>
          {env ? (
            <dl className="mt-3 space-y-2 text-[13px]">
              {ENV_DISPLAY_KEYS.map((key) => {
                const val = env[key];
                if (val === undefined || val === null || val === "") return null;
                return (
                  <div
                    key={key}
                    className="flex justify-between gap-3 border-b border-border/60 py-1.5 last:border-0"
                  >
                    <dt className="shrink-0 text-fg-subtle">{key}</dt>
                    <dd className="truncate text-right text-fg">
                      {typeof val === "object"
                        ? JSON.stringify(val)
                        : String(val)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : (
            <EmptyState title="无法读取环境配置" description="需要超级用户" />
          )}
          {userGlobal ? (
            <div className="mt-4 border-t border-border pt-3 text-[12px] text-fg-subtle">
              识别源：{String(userGlobal.RECOGNIZE_SOURCE ?? "—")} · 搜索源：
              {String(userGlobal.SEARCH_SOURCE ?? "—")}
            </div>
          ) : null}
        </section>

        <section className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4">
          <h2 className="text-[13px] font-medium text-fg-muted">
            通知渠道
          </h2>
          <p className="mt-1 text-[12px] text-fg-subtle">
            仅切换启用状态；Bot Token / Webhook 等请在服务端或官方配置中修改。
          </p>
          {notifications.length === 0 ? (
            <div className="mt-4">
              <EmptyState title="未配置通知渠道" />
            </div>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {notifications.map((n) => (
                <li
                  key={n.name || n.type}
                  className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] border border-border bg-bg px-3 py-2"
                >
                  <div>
                    <div className="text-[13px] font-medium text-fg">
                      {n.name || "未命名"}
                    </div>
                    <div className="text-[12px] text-fg-subtle">
                      {n.type || "unknown"}
                      {n.has_config ? " · 已配置" : " · 未配置密钥"}
                      {n.enabled ? " · 启用中" : " · 已关闭"}
                    </div>
                  </div>
                  <Button
                    variant={n.enabled ? "ghost" : "primary"}
                    disabled={!n.name || busyName === n.name}
                    onClick={() => void toggleNotify(n)}
                  >
                    {n.enabled ? "关闭" : "开启"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
